// ---------------------------------------------------------------------------
// What to say when a lead's expansion has no messages in it
//
// REPORTED 17 August: opening individual leads to review their email drafts
// showed nothing at all. Not an empty state, not "still generating" — the
// "Generated Messages" card was absent, because it began
//
//   if (leadMessages.length === 0) return nothing;
//
// So the one screen built for checking a message before it reaches a real
// person is also the screen that says nothing when there is no message to
// check. The operator's only remaining route was Review → "Reply Send to All",
// which is the bulk path: exactly the one where a human cannot read a message
// first. A silent empty state pushed him from the careful route to the
// irreversible one.
//
// `nothing` is the correct render for a section that does not apply. It is the
// wrong render for a section that applies and is empty, and those two were the
// same branch.
//
// This module decides only WHICH sentence is true, so it can be tested without
// a DOM — the page renders whatever comes back. It states no explanation it
// cannot support: with the campaign mid-generation the honest answer is "not
// yet", and only once generation has finished is an empty list a gap.
// ---------------------------------------------------------------------------

/** What the lead's Generated Messages section should say when it holds nothing. */
export interface EmptyMessagesNotice {
  /** 'waiting' — generation is still running. 'gap' — it finished and produced none. */
  kind: 'waiting' | 'gap';
  title: string;
  detail: string;
}

/**
 * The notice for a lead with no generated messages, or `null` when the section
 * has messages and should render them.
 *
 * `campaignStatus` decides between the two honest readings. Anything before
 * `ready_to_launch` may simply not have reached this lead yet; at or after it,
 * generation has had its chance and an empty list is a lead that would be
 * skipped — which is a thing to say out loud, because the lead still appears in
 * every count on the page.
 */
export function emptyMessagesNotice(params: {
  messageCount: number;
  campaignStatus: string;
  leadHasEmail: boolean;
  leadHasLinkedin: boolean;
  campaignUsesEmail: boolean;
  campaignUsesLinkedin: boolean;
  /** The lead is on the Do Not Contact list. It is kept and shown, but owes no
   * message and is never contacted — so an empty section is expected, not a gap
   * to fix by regenerating. */
  leadOptedOut?: boolean;
}): EmptyMessagesNotice | null {
  if (params.messageCount > 0) return null;

  // An opted-out lead has no message on purpose. Say that, rather than sending
  // the operator to "regenerate or remove" a lead they must not contact.
  if (params.leadOptedOut) {
    return {
      kind: 'gap',
      title: 'This lead has opted out',
      detail: 'On the Do Not Contact list — no message is generated and nothing will be sent to them.',
    };
  }

  if (params.campaignStatus === 'draft' || params.campaignStatus === 'generating') {
    return {
      kind: 'waiting',
      title: 'Messages are still being written',
      detail: 'This lead has not been reached yet. The section fills in as generation gets to it.',
    };
  }

  // Generation is over. Name the likeliest reason from what this lead actually
  // has, rather than reporting only that the list is empty — the same rule the
  // diagnostics route's `blockedBy` follows.
  const missing: string[] = [];
  if (params.campaignUsesEmail && !params.leadHasEmail) missing.push('no email address');
  if (params.campaignUsesLinkedin && !params.leadHasLinkedin) missing.push('no LinkedIn URL');

  return {
    kind: 'gap',
    title: 'No messages were generated for this lead',
    detail:
      missing.length > 0
        ? `This lead has ${missing.join(' and ')}, which this campaign’s steps need. Nothing will be sent to them.`
        : 'Generation has finished and produced nothing for this lead, so nothing will be sent to them. ' +
          'Regenerate the campaign’s messages, or remove the lead.',
  };
}
